import { useCallback, useMemo, useState } from 'react';
import useFlatPages from './useFlatPages';
import { isEqual } from 'lodash';

type AnyRecord = Record<string, unknown>;

export const normalizeFilter = (value: unknown): unknown => {
	if (Array.isArray(value)) {
		return value.map(normalizeFilter).filter(item => item !== undefined);
	}

	if (value !== null && typeof value === 'object') {
		const result: AnyRecord = {};
		for (const [key, valueItem] of Object.entries(value)) {
			const normalizedValue = normalizeFilter(valueItem);
			if (normalizedValue !== undefined) result[key] = normalizedValue;
		}
		return Object.keys(result).length ? result : {};
	}

	return value === undefined ? undefined : value;
};

export function useFlatDataList<TItem extends { id: string }>(
	filter: object = {},
	take?: number,
) {
	const { pagination, getNextPage, setPage, page } = useFlatPages(take);
	const [flatData, setFlatData] = useState<TItem[]>([]);

	const normalizedFilter = useMemo(
		() => normalizeFilter(filter) ?? {},
		[filter],
	);

	const reset = useCallback(() => {
		setFlatData([]);
		setPage(1);
	}, [setPage]);

	const [prevFilter, setPrevFilter] = useState(normalizedFilter);
	if (!isEqual(prevFilter, normalizedFilter)) {
		setPrevFilter(normalizedFilter);
		reset();
	}

	const appendData = useCallback(
		(data: TItem[] | undefined, flush?: boolean) => {
			if (!data?.length) return;

			const shouldFlush = flush ?? page === 1;

			setFlatData(prev => {
				const base = shouldFlush ? [] : prev;
				const seen = new Set(base.map(i => i.id));

				const toAdd = data.filter(item => {
					if (seen.has(item.id)) return false;
					seen.add(item.id);
					return true;
				});

				return toAdd.length ? [...base, ...toAdd] : base;
			});
		},
		[page],
	);

	const updateItem = useCallback((item: TItem) => {
		setFlatData(prev =>
			prev.map(existing => (existing.id === item.id ? item : existing)),
		);
	}, []);

	const replaceData = useCallback(
		(data: TItem[] | undefined) => {
			setFlatData(data ?? []);
			setPage(1);
		},
		[setPage],
	);

	return {
		flatData,
		pagination,
		getNextPage,
		appendData,
		updateItem,
		replaceData,
		reset,
	};
}
